import { useMemo, useState } from "react";
import {
  ArrowLeft,
  ExternalLink,
  Youtube,
  FileText,
  BookMarked,
  Rss,
  Pill,
  Search,
  BookOpen,
  Brain,
  Plane,
  Leaf,
  Sparkles,
  Stethoscope,
  Library as LibraryIcon,
} from "lucide-react";
import { CATEGORIES, RESOURCES } from "@/data/resources";
import { ALL_LESSONS, getCourseBySlug } from "@/data/courses";
import ThemeToggle from "@/components/hormone/ThemeToggle";

const ICONS = {
  Youtube,
  FileText,
  BookMarked,
  Rss,
  Pill,
  BookOpen,
  Brain,
  Plane,
  Leaf,
  Sparkles,
  Stethoscope,
  Library: LibraryIcon,
};

// Build a flat searchable index of every lesson across all courses.
const LESSON_INDEX = ALL_LESSONS.map((loc) => {
  const courseCfg = getCourseBySlug(loc.courseSlug);
  const mod = courseCfg.course.modules[loc.moduleIndex];
  const l = mod.lessons[loc.lessonIndex];
  const sectionsText = l.content.sections
    .map((s) => `${s.heading}\n${s.body || ""}`)
    .join("\n");
  const flashText = l.content.flashcards
    .map((f) => `${f.q} ${f.a}`)
    .join("\n");
  return {
    id: `lesson-${l.id}`,
    type: "lesson",
    lessonId: l.id,
    title: l.title,
    source: `${courseCfg.course.title} · ${mod.title} · ${l.duration}`,
    summary: l.content.intro,
    haystack: [
      l.title,
      l.content.intro,
      sectionsText,
      flashText,
    ]
      .join(" ")
      .toLowerCase(),
  };
});

function LessonCard({ item }) {
  return (
    <a
      href={`#/lesson/${item.lessonId}`}
      data-testid={`resource-${item.id}`}
      className="card card-hover rise"
      style={{
        display: "block",
        padding: 20,
        marginBottom: 12,
        color: "var(--text)",
        textDecoration: "none",
        borderLeft: "3px solid var(--mod-2)",
      }}
    >
      <header
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background:
              "color-mix(in srgb, var(--mod-2) 16%, transparent)",
            color: "var(--mod-2)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BookOpen size={18} strokeWidth={1.75} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="t-meta"
            style={{ color: "var(--muted)", marginBottom: 2, fontSize: 11 }}
          >
            Lesson
          </div>
          <h3
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: "var(--text)",
              lineHeight: 1.35,
            }}
          >
            {item.title}
          </h3>
        </div>
      </header>
      <p
        style={{
          margin: "0 0 8px",
          fontSize: 14,
          color: "var(--text-2)",
          lineHeight: 1.55,
        }}
      >
        {item.summary}
      </p>
      <div className="t-meta" style={{ color: "var(--muted)", fontSize: 11 }}>
        {item.source}
      </div>
    </a>
  );
}

function ResourceCard({ item }) {
  const cat = CATEGORIES.find((c) => c.id === item.type);
  const Icon = ICONS[cat?.icon] || ExternalLink;

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`resource-${item.id}`}
      className="card card-hover rise"
      style={{
        display: "block",
        padding: 20,
        marginBottom: 12,
        color: "var(--text)",
        textDecoration: "none",
        borderLeft: "3px solid var(--accent)",
      }}
    >
      <header
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background:
              "color-mix(in srgb, var(--accent) 14%, transparent)",
            color: "var(--accent)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="t-meta"
            style={{
              color: "var(--muted)",
              marginBottom: 2,
              fontSize: 11,
            }}
          >
            {cat?.label}
            {item.year ? ` · ${item.year}` : ""}
          </div>
          <h3
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: "var(--text)",
              lineHeight: 1.35,
            }}
          >
            {item.title}
          </h3>
        </div>
        <ExternalLink
          size={16}
          strokeWidth={1.75}
          color="var(--muted)"
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        />
      </header>
      <p
        style={{
          margin: "0 0 8px",
          fontSize: 14,
          color: "var(--text-2)",
          lineHeight: 1.55,
        }}
      >
        {item.summary}
      </p>
      <div
        className="t-meta"
        style={{ color: "var(--muted)", fontSize: 11 }}
      >
        {item.author ? `${item.author} · ` : ""}
        {item.source}
      </div>
    </a>
  );
}

export default function ResourcesView({ onBack }) {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    // Resources (external)
    const resourceResults = RESOURCES.filter((r) => {
      if (cat !== "all" && cat !== "lesson" && r.type !== cat) return false;
      if (cat === "lesson") return false; // excluded when filtering lessons only
      if (!needle) return true;
      return [r.title, r.source, r.author, r.summary]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(needle));
    });

    // Lessons (in-app) — only show when there's a query or the lesson filter
    // is active, so the list isn't cluttered on first open.
    const showLessons =
      cat === "lesson" || (cat === "all" && needle.length > 0);
    const lessonResults = showLessons
      ? LESSON_INDEX.filter((l) => {
          if (!needle) return true;
          return l.haystack.includes(needle);
        })
      : [];

    return { resources: resourceResults, lessons: lessonResults };
  }, [cat, q]);

  const counts = useMemo(() => {
    const c = { all: RESOURCES.length, lesson: LESSON_INDEX.length };
    for (const r of RESOURCES) c[r.type] = (c[r.type] || 0) + 1;
    return c;
  }, []);

  const totalVisible = filtered.resources.length + filtered.lessons.length;

  const ALL_CATS = [
    { id: "all", label: "All" },
    { id: "lesson", label: "Lessons" },
    ...CATEGORIES,
  ];

  return (
    <>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: "var(--scrim)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto",
            padding: "8px 16px",
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            gap: 16,
            minHeight: 56,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            className="btn btn-ghost touch"
            data-testid="library-back-btn"
            aria-label="Back to course"
            style={{ padding: "0 14px", minHeight: 40, fontSize: 14 }}
          >
            <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />
            Course
          </button>
          <div
            className="t-meta"
            style={{
              textAlign: "center",
              color: "var(--muted)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Library · research &amp; references
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "24px 16px 80px",
        }}
        data-testid="library-view"
      >
        <section className="rise" style={{ marginBottom: 24 }}>
          <h1
            className="t-display"
            data-testid="library-title"
            style={{ margin: "0 0 8px" }}
          >
            Library
          </h1>
          <p className="t-body" style={{ margin: 0, maxWidth: 560 }}>
            Search lessons, flashcards, and curated external references —
            videos, peer-reviewed papers, journals, blogs, and supplement
            research.
          </p>
        </section>

        {/* Search */}
        <div
          className="card rise"
          style={{
            padding: "0 14px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 10,
            minHeight: 48,
          }}
        >
          <Search
            size={16}
            strokeWidth={1.75}
            color="var(--muted)"
            aria-hidden="true"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search lessons, flashcards, titles, authors…"
            aria-label="Search library"
            data-testid="library-search-input"
            style={{
              flex: 1,
              background: "transparent",
              border: 0,
              outline: 0,
              color: "var(--text)",
              fontFamily: "var(--font-body)",
              fontSize: 15,
              minHeight: 44,
            }}
          />
        </div>

        {/* Category filter */}
        <nav
          aria-label="Filter by category"
          role="tablist"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 24,
          }}
        >
          {ALL_CATS.map((c) => {
            const active = cat === c.id;
            return (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setCat(c.id)}
                data-testid={`library-filter-${c.id}`}
                className="touch"
                style={{
                  minHeight: 40,
                  padding: "0 14px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  fontFamily: "var(--font-body)",
                  color: active ? "#fff" : "var(--text)",
                  background: active
                    ? "var(--accent)"
                    : "var(--surface)",
                  border: `1px solid ${
                    active ? "var(--accent)" : "var(--border)"
                  }`,
                  transition:
                    "background-color var(--dur) var(--ease), color var(--dur) var(--ease), border-color var(--dur) var(--ease)",
                }}
              >
                {c.label}
                <span
                  className="t-meta"
                  style={{
                    marginLeft: 8,
                    color: active
                      ? "rgba(255,255,255,0.85)"
                      : "var(--muted)",
                    fontSize: 10,
                  }}
                >
                  {counts[c.id] || 0}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Results */}
        <div data-testid="library-results">
          {totalVisible === 0 ? (
            <div
              className="card"
              style={{
                padding: 32,
                textAlign: "center",
                color: "var(--muted)",
              }}
              data-testid="library-empty"
            >
              No resources match your search.
            </div>
          ) : (
            <>
              {filtered.lessons.length > 0 && (
                <section
                  aria-label="In-app lessons"
                  style={{ marginBottom: filtered.resources.length ? 24 : 0 }}
                >
                  <div
                    className="t-meta"
                    style={{ color: "var(--muted)", marginBottom: 8 }}
                  >
                    From the course · {filtered.lessons.length}
                  </div>
                  {filtered.lessons.map((item) => (
                    <LessonCard key={item.id} item={item} />
                  ))}
                </section>
              )}
              {filtered.resources.length > 0 && (
                <section aria-label="External resources">
                  {filtered.lessons.length > 0 && (
                    <div
                      className="t-meta"
                      style={{ color: "var(--muted)", marginBottom: 8 }}
                    >
                      External · {filtered.resources.length}
                    </div>
                  )}
                  {filtered.resources.map((item) => (
                    <ResourceCard key={item.id} item={item} />
                  ))}
                </section>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}
